import React, { useState, useCallback, useMemo } from "react"
import { Button, Grid, InputAdornment, TextField } from "@material-ui/core"
import { ActionDialog, Notification } from "@/components"
import { FilterList as FilterListIcon, HourglassEmpty as HourglassIcon, Search as SearchIcon } from "@material-ui/icons"
import useCustomStyles from "@/styles/custom"
import ContactFiltersList from "@/pages/Admin/ClientCatalog/ClientManageFilters/ContactsFilters/ContactsFiltersList"
import ManageContactFilter from "@/pages/Admin/ClientCatalog/ClientManageFilters/ContactsFilters/ManageContactFilter"
import { ClientCatalogWhereData } from "@/pages/Admin/ClientCatalog"
import { BaseContactsFiltersType } from "@/protocols/ClientCatalogFilterProtocol"
import { useGlobalStateStore } from "@/store/GlobalState"
import ErrorHandlerService from "@/services/ErrorHandler"
import { ErrorType } from "@/hooks/useValidation"
import useDidMount from "@/hooks/useDidMount"
import useStyles from "@/pages/Admin/ClientCatalog/ClientManageFilters/ContactsFilters/styles"
import FilterService from "@/services/FilterService"

export type ContactFilterMode = "edit" | "create" | "temporary" | "editTemporary" | "list"

export type ContactFilterModeData = {
	onSave?: (filters?: BaseContactsFiltersType) => Promise<void>
	onClose?: () => void
	onSecondaryAction?: (filters?: BaseContactsFiltersType) => Promise<void>
	saveText: string
	secondaryActionText?: string
	title: string
	disableNameInput: boolean
}

type ContactFilterProps = {
	getDataWithFilters: (newWhereData?: Partial<ClientCatalogWhereData>) => Promise<void>
	handleUpdateFilterData: (updatedFilter: BaseContactsFiltersType) => void
	filter?: BaseContactsFiltersType
}

const ContactFilters: React.FC<ContactFilterProps> = ({
	getDataWithFilters,
	handleUpdateFilterData,
	filter
}) => {
	const [search, setSearch] = useState("")
	const [loading, setLoading] = useState(false)
	const [filterList, setFilterList] = useState([] as BaseContactsFiltersType[])
	const [openListFiltersDialog, setOpenListFiltersDialog] = useState(false)

	const customClasses = useCustomStyles()
	const classes = useStyles()
	const inboxChannelId = useGlobalStateStore().currentChannel?.id
	const hasCurrentFilterActive = Boolean(filter)

	const handleOpenFilterListDialog = () => {
		setOpenListFiltersDialog(true)
	}
	const handleCloseFilterListDialog = () => {
		setOpenListFiltersDialog(false)
	}

	const useFilters = async (selectedFilter?: BaseContactsFiltersType) => {
		handleCloseFilterListDialog()
		try {
			await getDataWithFilters({
				contactsFilters: {
					conditions: selectedFilter?.conditions,
					mainOperator: selectedFilter?.mainOperator,
					title: selectedFilter?.title,
					id: selectedFilter?.id,
					inboxChannelId
				}
			})

			if (selectedFilter) {
				handleUpdateFilterData(selectedFilter)
			}
		} catch (error) {
			Notification.error({ message: "Houve um erro ao aplicar os filtros" })
		}
	}

	const saveNewFilter = async (newFilter?: BaseContactsFiltersType) => {
		if (!newFilter) return

		try {
			const formattedFilter = { ...newFilter, title: newFilter.title?.trim() || "", inboxChannelId }
			const newFilterData = await FilterService.post(formattedFilter)
			const savedFilter = {
				...formattedFilter,
				id: newFilterData.id,
				title: newFilterData.title?.trim() || ""
			}

			setFilterList(lastState => {
				return [
					...lastState,
					savedFilter
				]
			})
			useFilters(savedFilter)
			Notification.success({ message: "Filtro salvo com sucesso!" })
		} catch (error) {
			Notification.error({ message: "Houve um erro ao salvar o filtro" })
		}
	}

	const saveEditedFilter = async (editedFilter?: BaseContactsFiltersType) => {
		if (!editedFilter || !editedFilter.id) return

		try {
			const formattedFilter = { ...editedFilter, title: editedFilter.title?.trim() || "", inboxChannelId }
			await FilterService.update(formattedFilter)

			useFilters(formattedFilter)
			setFilterList(lastState => {
				return lastState.map(filter =>
					filter.id === formattedFilter.id ? formattedFilter : filter
				)
			})
			Notification.success({ message: "Filtro atualizado com sucesso!" })
		} catch (error) {
			console.error("Error saving edited filter:", error)
			Notification.error({ message: "Houve um erro ao salvar o filtro" })
		}
	}

	const contactFilterModeToOptions: Record<ContactFilterMode, ContactFilterModeData> = {
		create: {
			title: "Criar filtro",
			saveText: "SALVAR E FILTRAR",
			disableNameInput: false,
			onSave: saveNewFilter,
			onClose: () => ManageContactFilter.close()
		},
		edit: {
			title: "Alterar filtro",
			saveText: "SALVAR E FILTRAR",
			disableNameInput: false,
			onSave: saveEditedFilter,
			onClose: () => ManageContactFilter.close()
		},
		editTemporary: {
			title: "Alterar filtro temporário",
			saveText: "FILTRAR",
			disableNameInput: false,
			onSave: useFilters,
			onSecondaryAction: saveNewFilter,
			secondaryActionText: "FILTRAR E SALVAR",
			onClose: () => ManageContactFilter.close()
		},
		temporary: {
			title: "Filtro temporário",
			saveText: "FILTRAR",
			disableNameInput: true,
			onSave: useFilters,
			onClose: () => ManageContactFilter.close()
		},
		list: {
			title: "",
			saveText: "",
			disableNameInput: true
		}
	}

	const handleOpenManageDialog = (mode: ContactFilterMode, initialFilter?: BaseContactsFiltersType) => {
		const options = contactFilterModeToOptions[mode]
		ManageContactFilter.open({
			filter: initialFilter,
			dialogOptions: options,
			handleUseFilters: useFilters,
			handleUpdateFilterData: handleUpdateFilterData
		})
	}

	const handleGetContactsFilters = async () => {
		setLoading(true)
		try {
			const filtersData = await FilterService.get(inboxChannelId)
			if (filtersData) {
				setFilterList(filtersData)
			}
		} catch (error) {
			Notification.error({ message: "Houve um erro ao buscar os filtros" })
			ErrorHandlerService.handle(error as ErrorType)
		}
		setLoading(false)
	}

	const filteredFilterList = useMemo(() => {
		if (!search) return filterList

		return filterList?.filter(filter => {
			const normalizedTitle = filter.title?.toLowerCase()

			return normalizedTitle?.includes(search.toLowerCase())
		})
	}, [filterList, search])

	const getEditMode = useCallback((): ContactFilterMode => {
		return filter?.id ? "edit" : "editTemporary"
	}, [JSON.stringify(filter)])

	useDidMount(() => {
		handleGetContactsFilters()
	})

	return (
		<>
			<Grid item>
				<Button
					variant="contained"
					startIcon={<FilterListIcon />}
					className={customClasses.primaryActionButton}
					onClick={() => hasCurrentFilterActive ? handleOpenManageDialog(getEditMode(), filter) : handleOpenFilterListDialog()}
				>
					{hasCurrentFilterActive ? "Alterar Filtro" : "Filtrar"}
				</Button>
			</Grid>

			<ActionDialog
				title="Filtros"
				openDialog={openListFiltersDialog}
				onClose={handleCloseFilterListDialog}
				hideCloseButton
				fullWidth
				maxWidth="md"
				dialogContentClassName={classes.dialogSize}
			>
				<Grid
					container
					direction="column"
					justifyContent="flex-end">
					<Grid item>
						<Grid
							container
							justifyContent="space-between"
							spacing={1}>
							<Grid item xs={7}>
								<TextField
									fullWidth
									placeholder="Pesquisar"
									variant="outlined"
									value={search}
									style={{ height: 53 }}
									InputProps={{
										style: { height: 53 },
										startAdornment: (
											<InputAdornment position="start">
												<SearchIcon />
											</InputAdornment>
										)
									}}
									onChange={e => setSearch(e.target.value)}
								/>
							</Grid>
							<Grid item xs={5}>
								<Grid container direction="row" spacing={1} justifyContent="flex-end">
									<Grid item xs={5}>
										<Button
											variant="contained"
											startIcon={<FilterListIcon />}
											style={{ width: "100%" }}
											className={customClasses.primaryActionButton}
											onClick={() => handleOpenManageDialog("create")}
										>
											Criar filtro
										</Button>
									</Grid>
									<Grid item xs={7}>
										<Button
											variant="contained"
											style={{ width: "100%" }}
											startIcon={<HourglassIcon />}
											className={customClasses.secondaryActionButton}
											onClick={() => handleOpenManageDialog("temporary")}
										>
											Filtro Temporário
										</Button>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>

					<Grid item>
						<ContactFiltersList
							filters={filteredFilterList}
							loading={loading}
							setFilters={setFilterList}
							handleUseFilters={(selectedFilter) => {
								useFilters(selectedFilter)
								handleCloseFilterListDialog()
							}}
							handleOpenEditFilterDialog={(editFilter) => {
								handleOpenManageDialog("edit", editFilter)
							}}
						/>
					</Grid>
				</Grid>
			</ActionDialog>
		</>
	)
}

export default ContactFilters
